import { formatSiteDate, formatSiteTime, parseSiteDateTimeLocalInput } from '../siteTime/siteTime';

export const QUALIFYING_ENTRY_HEADING = 'REPEATING';
export const QUALIFYING_ENTRY_METRIC = 'Restricted-area entries';
export const QUALIFYING_ENTRY_HELPER =
  'Qualifying entry events. The same person may produce more than one entry.';
export const QUALIFYING_ENTRY_POLL_MS = 5_000;
export const QUALIFYING_ENTRY_BAR_COUNT = 12;
export const FIVE_MINUTES_MS = 5 * 60 * 1000;
/**
 * Finalized history is late only after this long. The measurement cadence is
 * 15 minutes; 60 minutes leaves room for normal settlement and is not a new
 * monitoring service.
 */
export const HISTORY_FINALIZATION_DELAY_MS = 60 * 60 * 1000;
export const OBSERVED_CHANGE_QUALIFIER =
  'Observed change between measured periods; not proof that an intervention worked.';

export type QualifyingEntryEpisode = {
  episodeId: string;
  eventTime: string;
};

export type QualifyingLiveEpisode = {
  episodeId: string;
  qualifiedAt: string;
};

export type QualifyingPhysicalOccurrence = {
  physicalOccurrenceId: string;
  at: string;
  episodeIds: string[];
};

export type QualifyingEntryApiPayload = {
  ok?: boolean;
  episodes?: Array<{ episode_id?: string; event_time?: string }>;
  live_episodes?: Array<{ episode_id?: string; qualified_at?: string }>;
  live_occurrences?: Array<{
    physical_occurrence_id?: string;
    qualified_at?: string;
    episode_ids?: string[];
    identity?: string;
  }>;
  finalized_occurrences?: Array<{
    physical_occurrence_id?: string;
    event_time?: string;
    episode_ids?: string[];
    identity?: string;
  }>;
  identity_unverified_episodes?: Array<{ episode_id?: string; qualified_at?: string }>;
  buckets?: Array<{ bucket_start?: string; bucket_end?: string; finality?: string; usable_camera_seconds?: number | null; coverage_outcome?: string | null; basis_version?: string | null; measurement_epoch?: string | null }>;
  finalized_through?: string | null;
  finalized_coverage?: 'continuous' | 'incomplete' | 'unavailable' | string | null;
  period_comparison?: PeriodComparisonPayload | null;
  exploratory_operational_rate?: ExploratoryOperationalRate | null;
};

export type QualifyingEntryParentBucket = {
  bucketStart: string;
  bucketEnd: string;
  finality: 'finalized' | 'unavailable';
  usableCameraSeconds?: number | null;
  coverageOutcome?: string | null;
  basisVersion?: string | null;
  measurementEpoch?: string | null;
};

export type QualifyingEntryBarPresentation = 'future' | 'finalized-zero' | 'open' | 'missing' | 'activity';

export type QualifyingEntryBar = {
  key: string;
  startMs: number;
  endMs: number;
  finalizedCount: number;
  provisionalCount: number;
  future: boolean;
  covered: boolean;
  presentation: QualifyingEntryBarPresentation;
  label: string;
};

export type QualifyingEntryAxisLabel = {
  key: string;
  label: string;
  percent: number;
};

export type QualifyingEntryActivityStatus = 'loading' | 'empty' | 'error' | 'ready' | 'unavailable';

export type QualifyingEntryActivityView = {
  status: QualifyingEntryActivityStatus;
  todayCount: number | null;
  officialFinalizedCount: number | null;
  prominentCount: number | null;
  prominentKind: 'live-provisional' | 'finalized';
  prominentCaption: string;
  measurementInProgress: boolean;
  identityUnverifiedCount: number;
  todayLabel: string;
  updatedThroughLabel: string;
  boothHoursLabel: string;
  nowMarkerPercent: number;
  showNowMarker: boolean;
  axisLabels: QualifyingEntryAxisLabel[];
  bars: QualifyingEntryBar[];
  buckets: QualifyingEntryParentBucket[];
  helper: string;
  comparison?: PeriodComparison | null;
  selectedLocalDay: string;
  isToday: boolean;
  dateLabel: string;
  countCaption: string;
  countDistinction: string;
  historyDelayedLabel: string | null;
  asOfMs: number;
  timeZone: string | null;
  exploratoryRateLabel?: string | null;
};

export type PeriodDisplayStatus = 'FINALIZED' | 'PARTIAL' | 'IN PROGRESS' | 'UNAVAILABLE';

export type PeriodCoverage = 'partial' | 'complete' | 'unavailable';

export type PeriodSummary = {
  label: string;
  count: number | null;
  usableHours: number | null;
  rate: number | null;
  coverage: PeriodCoverage;
  includedBucketCount: number;
  basis: string | null;
  epoch: string | null;
};

export type PeriodComparison = {
  morning: PeriodSummary;
  afternoon: PeriodSummary;
  percentChange: number | null;
  comparable: boolean;
};

export function officialFinalizedCount(comparison: PeriodComparison | null | undefined): number | null {
  if (!comparison) return null;
  const morning = comparison.morning?.count;
  const afternoon = comparison.afternoon?.count;
  if (morning == null && afternoon == null) return null;
  return (morning ?? 0) + (afternoon ?? 0);
}

/** Today stays open until both local halves are complete. A historical day is closed only when coverage is complete. */
export function measurementWindowIncomplete(args: {
  isToday: boolean;
  finalizedCoverage?: string | null;
  comparison?: PeriodComparison | null;
}): boolean {
  const morning = args.comparison?.morning?.coverage;
  const afternoon = args.comparison?.afternoon?.coverage;
  const halvesComplete = morning === 'complete' && afternoon === 'complete';
  if (args.isToday) return !halvesComplete;
  if (halvesComplete) return false;
  const reported = String(args.finalizedCoverage || '').trim();
  return reported !== 'continuous' && reported !== 'complete';
}

function prominentHeadline(args: {
  isToday: boolean;
  todayCount: number | null;
  officialCount: number | null;
  finalizedCoverage?: string | null;
  comparison?: PeriodComparison | null;
}): Pick<QualifyingEntryActivityView, 'prominentCount' | 'prominentKind' | 'prominentCaption' | 'measurementInProgress'> {
  const measurementInProgress = measurementWindowIncomplete({
    isToday: args.isToday,
    finalizedCoverage: args.finalizedCoverage,
    comparison: args.comparison,
  });
  const useFinalizedHeadline = !args.isToday && !measurementInProgress;
  return {
    measurementInProgress,
    prominentKind: useFinalizedHeadline ? 'finalized' : 'live-provisional',
    prominentCount: useFinalizedHeadline ? args.officialCount : args.todayCount,
    prominentCaption: useFinalizedHeadline ? OFFICIAL_COUNT_CAPTION : LIVE_PROVISIONAL_CAPTION,
  };
}

type PeriodComparisonPayloadPeriod = {
  label?: string;
  count?: number | null;
  usable_camera_hours?: number | null;
  rate?: number | null;
  included_bucket_count?: number | null;
  coverage?: string | null;
  basis?: string | null;
  epoch?: string | null;
};

type PeriodComparisonPayload = {
  morning?: PeriodComparisonPayloadPeriod | null;
  afternoon?: PeriodComparisonPayloadPeriod | null;
  percent_change?: number | null;
  comparable?: boolean | null;
};

export type ExploratoryOperationalRate = {
  applies?: boolean;
  label?: string | null;
  recorded_occurrence_total?: number | null;
  usable_camera_seconds?: number | null;
};

/** Display line for the field-validation rate. It is not the official finalized count. */
export function exploratoryRateLabel(raw: ExploratoryOperationalRate | null | undefined): string | null {
  if (!raw || raw.applies !== true || raw.label == null || raw.recorded_occurrence_total == null) return null;
  const seconds = finiteNumber(raw.usable_camera_seconds);
  if (seconds == null) return null;
  const status = raw.label === 'coverage-limited' ? 'coverage-limited' : 'exploratory';
  return `${EXPLORATORY_RATE_CAPTION} · ${status} · ${raw.recorded_occurrence_total} / ${seconds} s`;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'boolean' || value == null || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatPeriod(raw: PeriodComparisonPayloadPeriod | null | undefined, fallbackLabel: string): PeriodSummary {
  const reported = raw?.coverage === 'complete' || raw?.coverage === 'partial' ? raw.coverage : 'unavailable';
  const rate = finiteNumber(raw?.rate);
  const count = finiteNumber(raw?.count);
  const usableHours = finiteNumber(raw?.usable_camera_hours);
  const measured = reported !== 'unavailable' && rate != null && count != null && usableHours != null;
  const basis = String(raw?.basis || '').trim();
  const epoch = String(raw?.epoch || '').trim();
  return {
    label: String(raw?.label || fallbackLabel).trim() || fallbackLabel,
    count: measured ? count : null,
    usableHours: measured ? usableHours : null,
    rate: measured ? rate : null,
    coverage: measured ? reported : 'unavailable',
    includedBucketCount: measured ? Math.max(0, Math.trunc(finiteNumber(raw?.included_bucket_count) || 0)) : 0,
    basis: measured && basis ? basis : null,
    epoch: measured && epoch ? epoch : null,
  };
}

/** Format the backend morning/afternoon pair. Does not recompute the rate. */
export function formatPeriodComparison(raw: PeriodComparisonPayload | null | undefined): PeriodComparison | null {
  if (!raw || typeof raw !== 'object') return null;
  const morning = formatPeriod(raw.morning, '12 AM–12 PM');
  const afternoon = formatPeriod(raw.afternoon, '12 PM–12 AM');
  const basisMatches = Boolean(
    morning.basis
    && morning.epoch
    && morning.basis === afternoon.basis
    && morning.epoch === afternoon.epoch,
  );
  const bothRates = morning.rate != null && afternoon.rate != null;
  const comparable = Boolean(raw.comparable) && bothRates && basisMatches;
  const percentChange = finiteNumber(raw.percent_change);
  return {
    morning,
    afternoon,
    comparable,
    percentChange: comparable && percentChange != null ? percentChange : null,
  };
}


const CALENDAR_AXIS_HOURS = [0, 3, 6, 9, 12, 15, 18, 21];
const HOUR_MS = 60 * 60 * 1000;
const MAX_QUERY_HOURS = 26;
export const COUNT_DISTINCTION = 'Live entries can differ because some are not yet finalized or are excluded by coverage rules.';
export const OFFICIAL_COUNT_CAPTION = 'FINALIZED MEASURED ENTRIES';
export const EXPLORATORY_RATE_CAPTION = 'EXPLORATORY OPERATIONAL RATE';
export const LIVE_COUNT_CAPTION = 'Live entries';
export const LIVE_PROVISIONAL_CAPTION = 'LIVE PROVISIONAL ENTRIES';
export const MEASUREMENT_IN_PROGRESS_LABEL = 'Measurement in progress';

export function measuredAreaLabel(args: {
  zoneName?: string | null;
  cameraName?: string | null;
}): string {
  const facing = (value: string | null | undefined, fallback: string) => {
    const text = String(value || '').trim();
    if (!text) return fallback;
    if (/^(cam_|rae_|pol_)/i.test(text) || /^[0-9a-f]{8}-[0-9a-f-]{20,}$/i.test(text)) return fallback;
    return text;
  };
  return `Measured area: Restricted-area entry · ${facing(args.zoneName, 'area name unavailable')} · ${facing(args.cameraName, 'camera name unavailable')}`;
}
const SETTLEMENT_DEADLINE_MS = 2 * 60 * 60 * 1000;

export function floorUtc(ms: number, step: number): number {
  return Math.floor(ms / step) * step;
}

export function siteCalendarDay(iso: string, timeZone?: string | null): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return '';
  const zone = String(timeZone || '').trim() || 'UTC';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || '';
  return `${read('year')}-${read('month')}-${read('day')}`;
}

const LOCAL_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function addSiteCalendarDays(day: string, delta: number): string {
  if (!LOCAL_DAY.test(day)) return day;
  const [year, month, date] = day.split('-').map(Number);
  const utc = new Date(Date.UTC(year, month - 1, date + delta));
  const nextYear = utc.getUTCFullYear();
  const nextMonth = String(utc.getUTCMonth() + 1).padStart(2, '0');
  const nextDate = String(utc.getUTCDate()).padStart(2, '0');
  return `${nextYear}-${nextMonth}-${nextDate}`;
}

/** Half-open site-local day [midnight, next midnight). Length stays within 26 hours, including DST. */
export function siteLocalDayWindow(
  day: string,
  timeZone?: string | null,
): { from: string; to: string } | null {
  if (!LOCAL_DAY.test(day)) return null;
  const from = parseSiteDateTimeLocalInput(`${day}T00:00`, timeZone);
  const to = parseSiteDateTimeLocalInput(`${addSiteCalendarDays(day, 1)}T00:00`, timeZone);
  if (!from || !to) return null;
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return null;
  if ((toMs - fromMs) / HOUR_MS > MAX_QUERY_HOURS) return null;
  return { from, to };
}

export function qualifyingEntryQueryWindow(
  nowMs: number,
  timeZone?: string | null,
  selectedLocalDay?: string | null,
): { from: string; to: string } {
  const today = siteCalendarDay(new Date(nowMs).toISOString(), timeZone);
  const selected = LOCAL_DAY.test(String(selectedLocalDay || '')) ? String(selectedLocalDay) : today;
  if (selected && today && selected < today) {
    const closed = siteLocalDayWindow(selected, timeZone);
    if (closed) return closed;
  }
  const chartEnd = floorUtc(nowMs, FIVE_MINUTES_MS);
  const chartStart = chartEnd - (QUALIFYING_ENTRY_BAR_COUNT * FIVE_MINUTES_MS);
  const midnight = today ? parseSiteDateTimeLocalInput(`${today}T00:00`, timeZone) : null;
  const midnightMs = midnight ? Date.parse(midnight) : chartStart;
  const fromMs = Math.min(chartStart, Number.isFinite(midnightMs) ? midnightMs : chartStart);
  return {
    from: new Date(fromMs).toISOString(),
    to: new Date(nowMs).toISOString(),
  };
}

export function showcaseSelectedDateLabel(day: string, isToday: boolean): string {
  const formatted = formatSiteDate(day) || day;
  return isToday ? `${formatted} (Today)` : formatted;
}

function bucketSupportsMeasuredHour(bucket: QualifyingEntryParentBucket): boolean {
  if (bucket.finality !== 'finalized') return false;
  const outcome = String(bucket.coverageOutcome || '').trim().toLowerCase();
  if (!outcome) return true;
  return outcome === 'sufficient';
}

export function latestFullyFinalizedHourEnd(
  buckets: QualifyingEntryParentBucket[],
  timeZone?: string | null,
  localDay?: string | null,
  nowMs?: number,
): number | null {
  const anchor = Number.isFinite(nowMs) ? Number(nowMs) : Date.now();
  const bounds = boothDayBounds(anchor, timeZone, localDay);
  if (!bounds) return null;
  let latest: number | null = null;
  let cursor = bounds.openMs;
  while (cursor < bounds.closeMs) {
    const startMs = cursor;
    const endMs = Math.min(bounds.closeMs, cursor + HOUR_MS);
    cursor = endMs;
    if (endMs <= startMs || endMs - startMs < HOUR_MS) continue;
    if (hourFullyCovered(buckets.filter(bucketSupportsMeasuredHour), startMs, endMs)) {
      latest = endMs;
    }
  }
  return latest;
}

export function finalizedCoverageLabel(args: {
  buckets: QualifyingEntryParentBucket[];
  finalizedThrough?: string | null;
  finalizedCoverage?: string | null;
  timeZone?: string | null;
  nowMs?: number;
  localDay?: string | null;
}): string {
  const through = String(args.finalizedThrough || '').trim();
  const finalizedEnds = args.buckets
    .filter((bucket) => bucket.finality === 'finalized')
    .map((bucket) => Date.parse(bucket.bucketEnd))
    .filter((value) => Number.isFinite(value));
  const latestMs = finalizedEnds.length
    ? Math.max(...finalizedEnds)
    : (through ? Date.parse(through) : Number.NaN);
  if (!Number.isFinite(latestMs)) return 'Finalized time unavailable.';
  const clock = formatSiteTime(new Date(latestMs).toISOString(), args.timeZone);
  const hourEnd = latestFullyFinalizedHourEnd(
    args.buckets,
    args.timeZone,
    args.localDay,
    args.nowMs,
  );
  const reported = String(args.finalizedCoverage || '').trim();
  const gap = reported === 'incomplete' || args.buckets.some((bucket) => {
    if (bucket.finality === 'finalized') return false;
    const end = Date.parse(bucket.bucketEnd);
    return Number.isFinite(end) && end <= latestMs;
  });
  const hourMatchesInterval = hourEnd != null && Math.abs(hourEnd - latestMs) < 1000;
  if (hourEnd != null && hourMatchesInterval && !gap && reported !== 'incomplete') {
    const hourClock = formatSiteTime(new Date(hourEnd).toISOString(), args.timeZone);
    return `Latest fully finalized hour ends at ${hourClock}.`;
  }
  return `Latest finalized interval ends at ${clock}. Coverage is incomplete.`;
}

export function historyDelayedLabel(args: {
  isToday: boolean;
  finalizedThrough?: string | null;
  nowMs: number;
  timeZone?: string | null;
}): string | null {
  if (!args.isToday) return null;
  const through = String(args.finalizedThrough || '').trim();
  if (!through) return null;
  const throughMs = Date.parse(through);
  if (!Number.isFinite(throughMs)) return null;
  if (args.nowMs - throughMs <= HISTORY_FINALIZATION_DELAY_MS) return null;
  const clock = formatSiteTime(through, args.timeZone);
  return `Historical measurements delayed — last finalized at ${clock}`;
}

export function periodDisplayStatus(
  period: PeriodSummary | null | undefined,
  args: {
    nowMs: number;
    timeZone?: string | null;
    selectedLocalDay: string;
    isToday: boolean;
    which: 'morning' | 'afternoon';
  },
): PeriodDisplayStatus {
  if (
    !period
    || period.coverage === 'unavailable'
    || period.rate == null
    || period.count == null
    || period.usableHours == null
  ) {
    return 'UNAVAILABLE';
  }
  if (period.coverage === 'complete') return 'FINALIZED';
  if (args.isToday && LOCAL_DAY.test(args.selectedLocalDay)) {
    const startHour = args.which === 'morning' ? 0 : 12;
    const endDay = args.which === 'morning' ? args.selectedLocalDay : addSiteCalendarDays(args.selectedLocalDay, 1);
    const endHour = args.which === 'morning' ? 12 : 0;
    const start = parseSiteDateTimeLocalInput(
      `${args.selectedLocalDay}T${String(startHour).padStart(2, '0')}:00`,
      args.timeZone,
    );
    const end = parseSiteDateTimeLocalInput(
      `${endDay}T${String(endHour).padStart(2, '0')}:00`,
      args.timeZone,
    );
    const startMs = start ? Date.parse(start) : Number.NaN;
    const endMs = end ? Date.parse(end) : Number.NaN;
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && args.nowMs >= startMs && args.nowMs < endMs) {
      return 'IN PROGRESS';
    }
  }
  return 'PARTIAL';
}

export function observedChangeCopy(comparison: PeriodComparison | null | undefined): {
  headline: string | null;
  unavailable: string | null;
  soFar: boolean;
} {
  const morning = comparison?.morning;
  const afternoon = comparison?.afternoon;
  const soFar = morning?.coverage === 'partial' || afternoon?.coverage === 'partial';
  const unavailable = 'Observed change is unavailable until both periods have a measured rate.';
  if (!comparison || !morning || !afternoon) {
    return { headline: null, unavailable, soFar: false };
  }
  if (comparison.comparable && comparison.percentChange != null) {
    const percent = comparison.percentChange;
    const rounded = Number(Math.abs(percent).toFixed(1));
    const magnitude = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
    const tail = soFar ? ' so far' : '';
    if (percent > 0) {
      return { headline: `Observed entry rate is ${magnitude}% higher in the PM${tail}.`, unavailable: null, soFar };
    }
    if (percent < 0) {
      return { headline: `Observed entry rate is ${magnitude}% lower in the PM${tail}.`, unavailable: null, soFar };
    }
    return {
      headline: `Observed entry rate is unchanged between AM and PM${tail}.`,
      unavailable: null,
      soFar,
    };
  }
  if (morning.rate === 0 && afternoon.rate != null && afternoon.rate !== 0) {
    return {
      headline: null,
      unavailable: 'Percent change is not available when the AM rate is zero and the PM rate is not.',
      soFar,
    };
  }
  if (
    morning.rate != null
    && afternoon.rate != null
    && (morning.basis !== afternoon.basis || morning.epoch !== afternoon.epoch)
  ) {
    return {
      headline: null,
      unavailable: 'Observed change is withheld because the periods do not share a measurement basis.',
      soFar,
    };
  }
  return { headline: null, unavailable, soFar };
}

function parentCovers(buckets: QualifyingEntryParentBucket[], eventMs: number): boolean {
  return buckets.some((bucket) => {
    if (bucket.finality !== 'finalized') return false;
    const start = Date.parse(bucket.bucketStart);
    const end = Date.parse(bucket.bucketEnd);
    return Number.isFinite(start) && Number.isFinite(end) && start <= eventMs && eventMs < end;
  });
}

function rangeLabel(startMs: number, endMs: number, timeZone?: string | null): string {
  const start = formatSiteTime(new Date(startMs).toISOString(), timeZone);
  const end = formatSiteTime(new Date(endMs).toISOString(), timeZone);
  return `${start}–${end}`;
}

function collapseOccurrences(rows: QualifyingPhysicalOccurrence[]): QualifyingPhysicalOccurrence[] {
  const byId = new Map<string, QualifyingPhysicalOccurrence>();
  for (const row of rows) {
    const physicalOccurrenceId = String(row.physicalOccurrenceId || '').trim();
    const at = String(row.at || '').trim();
    if (!physicalOccurrenceId || !at || Number.isNaN(Date.parse(at))) continue;
    const episodeIds = (row.episodeIds || [])
      .map((episodeId) => String(episodeId || '').trim())
      .filter(Boolean);
    const existing = byId.get(physicalOccurrenceId);
    if (!existing) {
      byId.set(physicalOccurrenceId, { physicalOccurrenceId, at, episodeIds: [...episodeIds] });
      continue;
    }
    const existingMs = Date.parse(existing.at);
    const nextMs = Date.parse(at);
    if (Number.isFinite(nextMs) && (!Number.isFinite(existingMs) || nextMs < existingMs)) {
      existing.at = at;
    }
    for (const episodeId of episodeIds) {
      if (!existing.episodeIds.includes(episodeId)) existing.episodeIds.push(episodeId);
    }
  }
  const collapsed: QualifyingPhysicalOccurrence[] = [];
  byId.forEach((occurrence) => {
    collapsed.push(occurrence);
  });
  return collapsed;
}

function mapPhysicalOccurrence(
  raw: {
    physical_occurrence_id?: string;
    qualified_at?: string;
    event_time?: string;
    episode_ids?: string[];
    identity?: string;
  },
  atKey: 'qualified_at' | 'event_time',
): QualifyingPhysicalOccurrence | null {
  const physicalOccurrenceId = String(raw.physical_occurrence_id || '').trim();
  const at = String(raw[atKey] || '').trim();
  const identity = String(raw.identity || '').trim();
  if (!physicalOccurrenceId || !at) return null;
  if (identity && identity !== 'entry_continuity') return null;
  return {
    physicalOccurrenceId,
    at,
    episodeIds: (raw.episode_ids || [])
      .map((episodeId) => String(episodeId || '').trim())
      .filter(Boolean),
  };
}

function compactSiteHour(iso: string, timeZone?: string | null): string {
  return formatSiteTime(iso, timeZone).replace(':00', '');
}

export function boothDayBounds(
  nowMs: number,
  timeZone?: string | null,
  localDay?: string | null,
): { day: string; openMs: number; closeMs: number; openIso: string; closeIso: string } | null {
  const today = siteCalendarDay(new Date(nowMs).toISOString(), timeZone);
  const day = LOCAL_DAY.test(String(localDay || '')) ? String(localDay) : today;
  if (!day) return null;
  const window = siteLocalDayWindow(day, timeZone);
  if (!window) return null;
  const openMs = Date.parse(window.from);
  const closeMs = Date.parse(window.to);
  if (!Number.isFinite(openMs) || !Number.isFinite(closeMs) || closeMs <= openMs) return null;
  return { day, openMs, closeMs, openIso: window.from, closeIso: window.to };
}

export function boothNowMarkerPercent(nowMs: number, timeZone?: string | null): number {
  const bounds = boothDayBounds(nowMs, timeZone);
  if (!bounds) return 0;
  const ratio = (nowMs - bounds.openMs) / (bounds.closeMs - bounds.openMs);
  if (!Number.isFinite(ratio)) return 0;
  return Math.min(100, Math.max(0, ratio * 100));
}

export function boothHoursContextLabel(nowMs: number, timeZone?: string | null, localDay?: string | null): string {
  const bounds = boothDayBounds(nowMs, timeZone, localDay);
  if (!bounds) return '';
  return `Hourly entries · ${compactSiteHour(bounds.openIso, timeZone)}–${compactSiteHour(bounds.closeIso, timeZone)}`;
}

export function boothAxisLabels(nowMs: number, timeZone?: string | null, localDay?: string | null): QualifyingEntryAxisLabel[] {
  const bounds = boothDayBounds(nowMs, timeZone, localDay);
  if (!bounds) return [];
  const span = bounds.closeMs - bounds.openMs;
  const ticks = CALENDAR_AXIS_HOURS.map((hour) => {
    const iso = parseSiteDateTimeLocalInput(
      `${bounds.day}T${String(hour).padStart(2, '0')}:00`,
      timeZone,
    );
    const tickMs = iso ? Date.parse(iso) : Number.NaN;
    const local = iso ? formatSiteTime(iso, timeZone) : '';
    const wanted = hour === 0 ? '12:00 AM' : null;
    if (!Number.isFinite(tickMs) || tickMs < bounds.openMs || tickMs > bounds.closeMs) return null;
    if (wanted && local && !local.startsWith('12:')) return null;
    const percent = ((tickMs - bounds.openMs) / span) * 100;
    return {
      key: String(hour),
      label: iso ? compactSiteHour(iso, timeZone) : '',
      percent,
    };
  }).filter((tick): tick is QualifyingEntryAxisLabel => tick != null);
  ticks.push({
    key: 'end',
    label: compactSiteHour(bounds.closeIso, timeZone),
    percent: 100,
  });
  return ticks;
}

function hourFullyCovered(
  buckets: QualifyingEntryParentBucket[],
  startMs: number,
  endMs: number,
): boolean {
  const spans = buckets
    .filter((bucket) => bucket.finality === 'finalized')
    .map((bucket) => ({ start: Date.parse(bucket.bucketStart), end: Date.parse(bucket.bucketEnd) }))
    .filter((span) => Number.isFinite(span.start) && Number.isFinite(span.end) && span.end > startMs && span.start < endMs)
    .sort((left, right) => left.start - right.start);
  let cursor = startMs;
  for (const span of spans) {
    if (span.start > cursor) return false;
    cursor = Math.max(cursor, span.end);
    if (cursor >= endMs) return true;
  }
  return cursor >= endMs;
}

function boothHourBars(args: {
  nowMs: number;
  timeZone?: string | null;
  localDay?: string | null;
  finalizedOccurrences: QualifyingPhysicalOccurrence[];
  liveOccurrences: QualifyingPhysicalOccurrence[];
  buckets: QualifyingEntryParentBucket[];
}): QualifyingEntryBar[] {
  const bounds = boothDayBounds(args.nowMs, args.timeZone, args.localDay);
  if (!bounds) return [];
  const finalizedIds = new Set(
    args.finalizedOccurrences
      .map((occurrence) => occurrence.physicalOccurrenceId)
      .filter(Boolean),
  );
  const latestFinalizedEnd = args.buckets.reduce((latest, bucket) => {
    if (bucket.finality !== 'finalized') return latest;
    const end = Date.parse(bucket.bucketEnd);
    if (!Number.isFinite(end)) return latest;
    return Math.max(latest, end);
  }, Number.NEGATIVE_INFINITY);
  const bars: QualifyingEntryBar[] = [];
  let cursor = bounds.openMs;
  while (cursor < bounds.closeMs && bars.length < MAX_QUERY_HOURS) {
    const startMs = cursor;
    const endMs = Math.min(bounds.closeMs, cursor + HOUR_MS);
    cursor = endMs;
    if (endMs <= startMs) break;
    const future = startMs > args.nowMs;
    const covered = hourFullyCovered(args.buckets.filter(bucketSupportsMeasuredHour), startMs, endMs);
    const pastSettlement = endMs + SETTLEMENT_DEADLINE_MS <= args.nowMs;
    const finalizedCount = args.finalizedOccurrences.filter((occurrence) => {
      const eventMs = Date.parse(occurrence.at);
      return Number.isFinite(eventMs)
        && eventMs >= startMs
        && eventMs < endMs
        && parentCovers(args.buckets, eventMs);
    }).length;
    const provisionalCount = args.liveOccurrences.filter((occurrence) => {
      const qualifiedMs = Date.parse(occurrence.at);
      return Number.isFinite(qualifiedMs)
        && qualifiedMs >= startMs
        && qualifiedMs < endMs
        && !parentCovers(args.buckets, qualifiedMs)
        && !finalizedIds.has(occurrence.physicalOccurrenceId);
    }).length;
    const span = rangeLabel(startMs, endMs, args.timeZone);
    const limited = args.buckets.some((bucket) => {
      const start = Date.parse(bucket.bucketStart);
      const end = Date.parse(bucket.bucketEnd);
      return bucket.finality === 'finalized'
        && String(bucket.coverageOutcome || '').trim().toLowerCase() === 'partial_claim_limited'
        && Number.isFinite(start)
        && Number.isFinite(end)
        && start < endMs
        && end > startMs;
    });
    const endedBeforeLatest = Number.isFinite(latestFinalizedEnd) && endMs <= latestFinalizedEnd;
    let presentation: QualifyingEntryBarPresentation = 'open';
    if (future) presentation = 'future';
    else if (finalizedCount > 0 || provisionalCount > 0) presentation = 'activity';
    else if (covered && endMs <= args.nowMs) presentation = 'finalized-zero';
    else if (endedBeforeLatest || pastSettlement) presentation = 'missing';
    const finalizedNoun = finalizedCount === 1 ? 'entry' : 'entries';
    let label = `${span}, not yet observed`;
    if (presentation === 'finalized-zero') {
      label = `${span}, 0 qualifying entries`;
    } else if (presentation === 'missing') {
      label = limited ? `${span}, coverage limited` : `${span}, coverage missing`;
    } else if (presentation === 'open') {
      label = `${span}, pending finalization`;
    } else if (presentation === 'activity') {
      const parts: string[] = [];
      if (finalizedCount > 0) parts.push(`${finalizedCount} qualifying ${finalizedNoun}`);
      if (provisionalCount > 0) {
        const pendingNoun = provisionalCount === 1 ? 'entry' : 'entries';
        parts.push(`${provisionalCount} ${pendingNoun} pending finalization`);
      }
      label = `${span}, ${parts.join(', ')}`;
    }
    bars.push({
      key: new Date(startMs).toISOString(),
      startMs,
      endMs,
      finalizedCount,
      provisionalCount,
      future,
      covered,
      presentation,
      label,
    });
  }
  return bars;
}

function dayContext(nowMs: number, timeZone?: string | null, selectedLocalDay?: string | null): {
  today: string;
  selected: string;
  isToday: boolean;
} {
  const today = siteCalendarDay(new Date(nowMs).toISOString(), timeZone);
  const requested = LOCAL_DAY.test(String(selectedLocalDay || '')) ? String(selectedLocalDay) : today;
  const selected = requested && today && requested > today ? today : (requested || today);
  return { today, selected, isToday: Boolean(selected) && selected === today };
}

function loadingView(): QualifyingEntryActivityView {
  return {
    status: 'loading',
    todayCount: null,
    officialFinalizedCount: null,
    prominentCount: null,
    prominentKind: 'live-provisional',
    prominentCaption: LIVE_PROVISIONAL_CAPTION,
    measurementInProgress: false,
    identityUnverifiedCount: 0,
    todayLabel: '',
    updatedThroughLabel: '',
    boothHoursLabel: '',
    nowMarkerPercent: 0,
    showNowMarker: false,
    axisLabels: [],
    bars: [],
    buckets: [],
    helper: QUALIFYING_ENTRY_HELPER,
    selectedLocalDay: '',
    isToday: true,
    dateLabel: '',
    countCaption: 'live physical entries today',
    countDistinction: '',
    historyDelayedLabel: null,
    asOfMs: 0,
    timeZone: null,
  };
}

export function buildQualifyingEntryActivityFromApi(
  payload: QualifyingEntryApiPayload,
  args: { nowMs: number; timeZone?: string | null; selectedLocalDay?: string | null },
): QualifyingEntryActivityView {
  if (payload.ok !== true) {
    return buildQualifyingEntryActivity({ status: 'error', nowMs: args.nowMs, timeZone: args.timeZone });
  }
  const buckets: QualifyingEntryParentBucket[] = (payload.buckets || [])
    .map((bucket) => ({
      bucketStart: String(bucket.bucket_start || '').trim(),
      bucketEnd: String(bucket.bucket_end || '').trim(),
      finality: bucket.finality === 'finalized' ? 'finalized' as const : 'unavailable' as const,
      usableCameraSeconds: bucket.usable_camera_seconds,
      coverageOutcome: bucket.coverage_outcome,
      basisVersion: bucket.basis_version,
      measurementEpoch: bucket.measurement_epoch,
    }))
    .filter((bucket) => bucket.bucketStart && bucket.bucketEnd);
  const finalizedOccurrences = (payload.finalized_occurrences || [])
    .map((occurrence) => mapPhysicalOccurrence(occurrence, 'event_time'))
    .filter((occurrence): occurrence is QualifyingPhysicalOccurrence => occurrence != null);
  const liveOccurrences = (payload.live_occurrences || [])
    .map((occurrence) => mapPhysicalOccurrence(occurrence, 'qualified_at'))
    .filter((occurrence): occurrence is QualifyingPhysicalOccurrence => occurrence != null);
  const identityUnverifiedEpisodes = (payload.identity_unverified_episodes || [])
    .map((episode) => ({
      episodeId: String(episode.episode_id || '').trim(),
      qualifiedAt: String(episode.qualified_at || '').trim(),
    }))
    .filter((episode) => episode.episodeId && episode.qualifiedAt);
  return buildQualifyingEntryActivity({
    status: 'ready',
    finalizedOccurrences,
    liveOccurrences,
    identityUnverifiedEpisodes,
    buckets,
    finalizedThrough: payload.finalized_through,
    finalizedCoverage: payload.finalized_coverage,
    nowMs: args.nowMs,
    timeZone: args.timeZone,
    selectedLocalDay: args.selectedLocalDay,
    periodComparison: formatPeriodComparison(payload.period_comparison),
    exploratoryRateLabel: exploratoryRateLabel(payload.exploratory_operational_rate),
  });
}

export function buildQualifyingEntryActivity(args: {
  status: 'loading' | 'error' | 'ready';
  episodes?: QualifyingEntryEpisode[] | null;
  liveEpisodes?: QualifyingLiveEpisode[] | null;
  finalizedOccurrences?: QualifyingPhysicalOccurrence[] | null;
  liveOccurrences?: QualifyingPhysicalOccurrence[] | null;
  identityUnverifiedEpisodes?: Array<{ episodeId: string; qualifiedAt: string }> | null;
  buckets?: QualifyingEntryParentBucket[] | null;
  finalizedThrough?: string | null;
  finalizedCoverage?: string | null;
  nowMs: number;
  timeZone?: string | null;
  selectedLocalDay?: string | null;
  periodComparison?: PeriodComparison | null;
  exploratoryRateLabel?: string | null;
}): QualifyingEntryActivityView {
  if (args.status === 'loading') return loadingView();
  if (args.status === 'error') {
    return {
      status: 'error',
      todayCount: null,
      officialFinalizedCount: null,
      prominentCount: null,
      prominentKind: 'live-provisional',
      prominentCaption: LIVE_PROVISIONAL_CAPTION,
      measurementInProgress: false,
      identityUnverifiedCount: 0,
      todayLabel: 'Today unavailable',
      updatedThroughLabel: 'Finalized time unavailable.',
      boothHoursLabel: '',
      nowMarkerPercent: 0,
      showNowMarker: false,
      axisLabels: [],
      bars: [],
      buckets: [],
      helper: QUALIFYING_ENTRY_HELPER,
      selectedLocalDay: '',
      isToday: true,
      dateLabel: '',
      countCaption: 'live physical entries today',
      countDistinction: '',
      historyDelayedLabel: null,
      asOfMs: args.nowMs,
      timeZone: args.timeZone || null,
    };
  }

  const buckets = Array.isArray(args.buckets) ? args.buckets : [];
  const { selected, isToday } = dayContext(args.nowMs, args.timeZone, args.selectedLocalDay);
  const liveForChart = collapseOccurrences(Array.isArray(args.liveOccurrences) ? args.liveOccurrences : [])
    .filter((occurrence) => siteCalendarDay(occurrence.at, args.timeZone) === selected);
  const finalizedOccurrences = collapseOccurrences(
    Array.isArray(args.finalizedOccurrences) ? args.finalizedOccurrences : [],
  );
  const bars = boothHourBars({
    nowMs: args.nowMs,
    timeZone: args.timeZone,
    localDay: selected,
    finalizedOccurrences,
    liveOccurrences: liveForChart,
    buckets,
  });

  const todayCount = liveForChart.length;
  const identityUnverifiedCount = (Array.isArray(args.identityUnverifiedEpisodes) ? args.identityUnverifiedEpisodes : [])
    .filter((episode) => {
      const qualifiedAt = String(episode.qualifiedAt || '').trim();
      return qualifiedAt && siteCalendarDay(qualifiedAt, args.timeZone) === selected;
    }).length;
  const anyFinalized = buckets.some((bucket) => bucket.finality === 'finalized');
  const anyBarCount = bars.some((bar) => bar.finalizedCount + bar.provisionalCount > 0);
  let status: QualifyingEntryActivityStatus = 'ready';
  if (!anyFinalized && todayCount === 0) status = 'unavailable';
  else if (!anyBarCount && todayCount === 0) status = 'empty';
  const officialCount = officialFinalizedCount(args.periodComparison);
  const throughLabel = finalizedCoverageLabel({
    buckets,
    finalizedThrough: args.finalizedThrough,
    finalizedCoverage: args.finalizedCoverage,
    timeZone: args.timeZone,
    nowMs: args.nowMs,
    localDay: selected,
  });
  const todayLabel = !isToday
    ? `${showcaseSelectedDateLabel(selected, false)}: ${todayCount}`
    : identityUnverifiedCount > 0
      ? `Today: ${todayCount} · identity unverified`
      : `Today: ${todayCount}`;
  return {
    status,
    todayCount,
    officialFinalizedCount: officialCount,
    ...prominentHeadline({
      isToday,
      todayCount,
      officialCount,
      finalizedCoverage: args.finalizedCoverage,
      comparison: args.periodComparison,
    }),
    identityUnverifiedCount,
    todayLabel,
    updatedThroughLabel: throughLabel,
    boothHoursLabel: boothHoursContextLabel(args.nowMs, args.timeZone, selected),
    nowMarkerPercent: isToday ? boothNowMarkerPercent(args.nowMs, args.timeZone) : 0,
    showNowMarker: isToday,
    axisLabels: boothAxisLabels(args.nowMs, args.timeZone, selected),
    bars,
    buckets,
    helper: QUALIFYING_ENTRY_HELPER,
    comparison: args.periodComparison ?? null,
    selectedLocalDay: selected,
    isToday,
    dateLabel: showcaseSelectedDateLabel(selected, isToday),
    countCaption: LIVE_COUNT_CAPTION,
    countDistinction: COUNT_DISTINCTION,
    historyDelayedLabel: historyDelayedLabel({
      isToday,
      finalizedThrough: args.finalizedThrough,
      nowMs: args.nowMs,
      timeZone: args.timeZone,
    }),
    asOfMs: args.nowMs,
    timeZone: args.timeZone || null,
    exploratoryRateLabel: args.exploratoryRateLabel ?? null,
  };
}
